import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { getUserFriendlyMessagesForErrorResponse } from '../../../../errors/user-friendly-error-message-service';

import EditShipFromModal from '../../../../ship-from/modals';
import { trackShipFromEditButtonClick } from '../../../../ship-from/tracking-events';
import type { ShippyPendingPrefill } from '../../../../store/shippy/shippy.actions';
import { EventSourceStrings } from '../../../../tracking/tracking-constants';
import messages from '../messages';
import useFetchShipFroms from '../use-fetch-ship-froms';
import { useShipFromPageErrorContext } from './ship-from-page-error-context-provider';

type EditShipFromModalContextValue = {
  setEditShipFromId: (id: string | null) => void;
};

type EditShipFromModalContextProviderProps = {
  children: React.ReactNode;
  autoEditShipFromId?: string | null;
  prefillAddress?: ShippyPendingPrefill['data'] | null;
};

const EditShipFromModalContext = React.createContext<EditShipFromModalContextValue | null>(null);
export const useEditShipFromModalContext = () => useContext(EditShipFromModalContext);

const EditShipFromModalContextProvider = ({
  children,
  autoEditShipFromId,
  prefillAddress = null,
}: EditShipFromModalContextProviderProps) => {
  const [shipFromId, setShipFromId] = useState<string | null>();
  const [isFromShippy, setIsFromShippy] = useState(false);
  const { setErrors } = useShipFromPageErrorContext() as { setErrors: (errors: unknown[]) => void };

  const onCloseEditShipFromModal = useCallback(() => {
    setShipFromId(null);
    setIsFromShippy(false);
  }, []);

  const value = useMemo(
    () => ({
      setEditShipFromId: setShipFromId,
    }),
    [setShipFromId],
  );

  useEffect(() => {
    if (autoEditShipFromId) {
      setShipFromId(autoEditShipFromId);
      setIsFromShippy(true);
      trackShipFromEditButtonClick(autoEditShipFromId, EventSourceStrings.SHIPPY_ASSISTANT);
    }
  }, [autoEditShipFromId]);

  const { fetchShipFroms } = useFetchShipFroms();
  const onSuccess = useCallback(async () => {
    setErrors([]);
    try {
      onCloseEditShipFromModal();
      await fetchShipFroms();
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        title: <FormattedMessage {...messages.editShipFromSuccessMessageTitle} />,
        content: '',
        duration: NOTIFICATION_DURATIONS.SHORT,
      });
    } catch (error) {
      const userFriendlyErrors = getUserFriendlyMessagesForErrorResponse(error as { Errors: never[]; errors: never[] })?.errors || [];
      setErrors(userFriendlyErrors);
    }
  }, [fetchShipFroms, onCloseEditShipFromModal, setErrors]);

  return (
    <EditShipFromModalContext.Provider value={value}>
      {children}
      {shipFromId && (
        <EditShipFromModal
          onClose={onCloseEditShipFromModal}
          onSuccess={onSuccess}
          prefillAddress={prefillAddress}
          shipFromId={shipFromId}
          source={isFromShippy ? EventSourceStrings.SHIPPY_ASSISTANT : EventSourceStrings.SETTINGS_SHIP_FROM_LOCATIONS}
        />
      )}
    </EditShipFromModalContext.Provider>
  );
};

export default EditShipFromModalContextProvider;
